'use client';

import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { buildUrl } from '@packages/shared/src/utils/buildUrl/buildUrl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useIntl } from 'react-intl';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type { MessageValue } from 'react-intl';
import { getPageNumbers } from './getPageNumbers';

const buttonClassName = 'text-color-text-darktransparent size-4.5 rounded-full border p-0';

export const PagingController = ({
  currentPage,
  totalPages,
}: {
  currentPage: number;
  totalPages: number;
}) => {
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { localizeUrl, language } = useI18n();
  const { formatMessage } = useIntl();

  const pageNumbers = getPageNumbers({ totalPages, currentPage });

  const onClickHandler = ({ updatedPage }: { updatedPage: number }) => {
    const searchParamsWithPage = new URLSearchParams(searchParams?.toString());
    searchParamsWithPage.set('page', updatedPage.toString());

    push(
      localizeUrl(
        buildUrl({
          pathname,
          searchParams: searchParamsWithPage,
        }),
        language,
      ),
    );

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="my-2.5 flex h-full w-full justify-between md:mx-auto md:w-[350px]">
      <Button
        aria-label={formatMessage({
          id: 'pagingController.ariaPreviousButton',
          defaultMessage: 'Zur vorherigen Seite der Bewertungen',
        })}
        disabled={currentPage <= 1}
        onClick={() => onClickHandler({ updatedPage: currentPage - 1 })}
        className={buttonClassName}
      >
        <span className="icon-emp-arrow-medium-left size-4" />
      </Button>
      {pageNumbers.map((page, index) => {
        if (page === '...') {
          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <span className="leading-4.5" key={index}>
              {page}
            </span>
          );
        }
        return (
          <Button
            aria-label={formatMessage<{ readonly page: MessageValue }>(
              { id: 'pagingController.ariaNumberButton', defaultMessage: 'Gehe zu Seite {page}' },
              { page },
            )}
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={index}
            onClick={() => onClickHandler({ updatedPage: page })}
            className={`h-4.5 w-4.5 rounded-full p-0 ${
              currentPage === page
                ? 'bg-color-primary text-color-primary-contrasttext border-color-primary-dark [&:not(:disabled)]:hover:bg-color-primary-dark'
                : 'bg-color-grey-light hover:bg-color-grey border-0'
            }`}
          >
            {page}
          </Button>
        );
      })}
      <Button
        aria-label={formatMessage({
          id: 'pagingController.ariaNextButton',
          defaultMessage: 'Zur nächsten Seite der Bewertungen',
        })}
        disabled={currentPage >= totalPages}
        onClick={() => onClickHandler({ updatedPage: currentPage + 1 })}
        className={buttonClassName}
      >
        <span className="icon-emp-arrow-medium-right size-4" />
      </Button>
    </div>
  );
};
